import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, writeBatch } from "firebase/firestore";
import { db } from "./firebase";
import { activityToFirestore } from "../models/activity";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "../services/collections";

/**
 * ActivityService
 * Centraliza as operações de banco de dados para o módulo de Atividades.
 */
const ActivityService = {
    /**
     * Busca todas as atividades de um tenant.
     */
    getActivities: async (idTenant) => {
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.ACTIVITIES),
                orderBy("order", "asc")
            );
            const querySnapshot = await getDocs(q);
            const activities = [];
            querySnapshot.forEach((doc) => {
                activities.push({ id: doc.id, ...doc.data() });
            });
            return activities;
        } catch (error) {
            console.error("Erro ao listar atividades:", error);
            throw error;
        }
    },

    /**
     * Salva ou atualiza uma atividade.
     */
    save: async (formData, idTenant, authUser, existingActivities = []) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const activityData = activityToFirestore(formData, idTenant, authUser);

        try {
            if (formData.id) {
                // UPDATE
                const activityRef = doc(db, "tenants", idTenant, COLLECTIONS.ACTIVITIES, formData.id);
                const oldActivity = existingActivities.find(a => a.id === formData.id) || {};
                await updateDoc(activityRef, activityData);

                // Auditoria
                const changes = AuditService.calculateDiff(oldActivity, activityData);
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.ACTIVITIES,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: formData.id,
                    entityName: activityData.name,
                    description: `Atualizou a atividade "${activityData.name}"`,
                    details: changes
                });

                return { id: formData.id, ...activityData };
            } else {
                // CREATE
                const newDocRef = await addDoc(collection(db, "tenants", idTenant, COLLECTIONS.ACTIVITIES), activityData);

                // Auditoria
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.ACTIVITIES,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: newDocRef.id,
                    entityName: activityData.name,
                    description: `Criou a atividade "${activityData.name}"`
                });

                return { id: newDocRef.id, ...activityData };
            }
        } catch (error) {
            console.error("Erro no ActivityService.save:", error);
            throw error;
        }
    },

    /**
     * Remove uma atividade.
     */
    delete: async (activityId, idTenant, authUser, activityName) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            await deleteDoc(doc(db, "tenants", idTenant, COLLECTIONS.ACTIVITIES, activityId));

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.ACTIVITIES,
                action: AUDIT_ACTIONS.DELETE,
                entityId: activityId,
                entityName: activityName,
                description: `Excluiu a atividade "${activityName || activityId}"`
            });

            return true;
        } catch (error) {
            console.error("Erro no ActivityService.delete:", error);
            throw error;
        }
    },

    /**
     * Reordena a lista de atividades em massa.
     */
    reorder: async (items, idTenant, authUser) => {
        if (!idTenant || !items.length) return;

        try {
            const batch = writeBatch(db);
            items.forEach((item, index) => {
                const docRef = doc(db, "tenants", idTenant, COLLECTIONS.ACTIVITIES, item.id);
                batch.update(docRef, { order: index });
            });

            await batch.commit();

            // Auditoria simplificada do lote
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.ACTIVITIES,
                action: AUDIT_ACTIONS.UPDATE,
                description: `Reordenou a lista de atividades (${items.length} itens)`
            });

            return true;
        } catch (error) {
            console.error("Erro no ActivityService.reorder:", error);
            throw error;
        }
    }
};

export default ActivityService;
